/**
 * Reading the one file this product cannot get from an API.
 *
 * Every other number on these screens is fetched: spend, impressions, clicks,
 * all of it pulled from the platforms and reconciled against them. The business
 * outcome is the exception, because the only parties who could report it
 * automatically are the advertising platforms themselves, and they are the ones
 * whose contribution is in question. So it arrives as a spreadsheet.
 *
 * Which makes the parsing unusually load-bearing. A person exported this from
 * Excel and will not read a stack trace, and a file accepted with a subtly
 * wrong column becomes the number a model is fitted to. Every rejection here
 * names the line, the column and the value.
 *
 * `splitCsvLine`, `parseSpreadsheetNumber` and `looksMisencoded` are shared with
 * the agent importer rather than rewritten — they encode a year of learning
 * about what Excel actually emits (BOMs, ฿ signs, thousands separators,
 * parenthesised negatives, mojibake from a Windows-1252 save).
 *
 * @module services/outcomeImport/parseOutcomeCsv
 */

import {
  splitCsvLine,
  parseSpreadsheetNumber,
  looksMisencoded,
  type RowProblem,
  type TFn,
} from '@/services/brokerImport/parseBrokerCsv';

/** The only column every file must carry. Every metric is optional on its own. */
export const REQUIRED_COLUMNS = ['date'] as const;

/** At least one of these has to be present, or the file measures nothing. */
export const METRIC_COLUMNS = ['leads', 'bookings', 'sales', 'revenue'] as const;

export const OPTIONAL_COLUMNS = ['platform', ...METRIC_COLUMNS] as const;

/**
 * Where an outcome may be attributed.
 *
 * Not the database's `platform` enum: that vocabulary describes accounts this
 * product syncs from, and two of these are neither. `agent` is the sales team,
 * who have no API and appear in the agent importer instead; `other` is walk-ins,
 * events and referrals — everything with no channel at all. `analytics` is
 * deliberately absent, because web analytics is a way of measuring rather than
 * a place a sale comes from.
 */
export const PLATFORMS = ['facebook', 'google', 'tiktok', 'line', 'agent', 'other'] as const;
export type OutcomePlatform = (typeof PLATFORMS)[number];

export interface ParsedOutcomeRow {
  /** 1-based line in the file as the user sees it, header included. */
  line: number;
  date: string;
  /** Null means the row is the whole day's total. */
  platform: OutcomePlatform | null;
  leads: number | null;
  bookings: number | null;
  sales: number | null;
  revenue: number | null;
}

export interface OutcomeParseResult {
  rows: ParsedOutcomeRow[];
  problems: RowProblem[];
  /** Set when the whole file is unusable — nothing was parsed. */
  fatal: string | null;
  /** The span the file actually covers, read from the rows themselves. */
  period: { start: string; end: string } | null;
}

/** Strict, because a date a spreadsheet guessed at is worse than a rejection. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const isRealDate = (iso: string): boolean => {
  if (!ISO_DATE.test(iso)) return false;
  // Round-trips only for a date that exists: 2026-02-30 parses and comes back
  // as March 2nd, which would otherwise be silently accepted as February.
  const d = new Date(`${iso}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso;
};

export const parseOutcomeCsv = (text: string, t: TFn): OutcomeParseResult => {
  const problems: RowProblem[] = [];
  const empty = (fatal: string): OutcomeParseResult => ({ rows: [], problems: [], fatal, period: null });

  if (looksMisencoded(text)) return empty(t('outcomeImport.notUtf8'));

  // Excel prepends a BOM; left in place it becomes part of the first header name.
  const lines = text.replace(/^\uFEFF/, '').split(/\r\n|\n|\r/);
  const headerIndex = lines.findIndex((l) => l.trim() !== '');
  if (headerIndex === -1) return empty(t('outcomeImport.emptyFile'));

  const header = splitCsvLine(lines[headerIndex]).map((h) => h.toLowerCase());
  if (!header.includes('date')) {
    return empty(t('outcomeImport.needDateColumn'));
  }
  const metricsPresent = METRIC_COLUMNS.filter((c) => header.includes(c));
  if (metricsPresent.length === 0) {
    return empty(t('outcomeImport.needAMetric', { columns: METRIC_COLUMNS.join(', ') }));
  }

  const at = (cells: string[], col: string): string => {
    const i = header.indexOf(col);
    return i === -1 ? '' : (cells[i] ?? '');
  };

  const rows: ParsedOutcomeRow[] = [];
  /** date -> whether that date has seen a total row and/or platform rows. */
  const shapeByDate = new Map<string, { total: number; split: number }>();
  /** Catches the same (date, platform) twice in one file. */
  const seen = new Map<string, number>();

  for (let i = headerIndex + 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') continue; // trailing newline, or a spacer row
    const lineNo = i + 1;
    const cells = splitCsvLine(line);

    const date = at(cells, 'date');
    if (!isRealDate(date)) {
      problems.push({ line: lineNo, column: 'date', message: t('outcomeImport.badDate', { value: date || '—' }) });
      continue;
    }

    const rawPlatform = at(cells, 'platform').toLowerCase();
    let platform: OutcomePlatform | null = null;
    if (rawPlatform !== '') {
      if (!(PLATFORMS as readonly string[]).includes(rawPlatform)) {
        problems.push({
          line: lineNo,
          column: 'platform',
          message: t('outcomeImport.badPlatform', { value: rawPlatform, allowed: PLATFORMS.join(', ') }),
        });
        continue;
      }
      platform = rawPlatform as OutcomePlatform;
    }

    const key = `${date}|${platform ?? '*'}`;
    const firstSeen = seen.get(key);
    if (firstSeen !== undefined) {
      problems.push({
        line: lineNo,
        column: 'date',
        message: t('outcomeImport.duplicateRow', { date, line: firstSeen }),
      });
      continue;
    }

    let bad = false;
    const metric = (col: string, whole: boolean): number | null => {
      const raw = at(cells, col);
      if (raw === '') return null;
      const n = parseSpreadsheetNumber(raw);
      if (n === null) {
        problems.push({ line: lineNo, column: col, message: t('outcomeImport.notANumber', { value: raw }) });
        bad = true;
        return null;
      }
      if (n < 0) {
        problems.push({ line: lineNo, column: col, message: t('outcomeImport.notNegative') });
        bad = true;
        return null;
      }
      if (whole && !Number.isInteger(n)) {
        problems.push({ line: lineNo, column: col, message: t('outcomeImport.wholeNumber') });
        bad = true;
        return null;
      }
      return n;
    };

    const leads = metric('leads', true);
    const bookings = metric('bookings', true);
    const sales = metric('sales', true);
    const revenue = metric('revenue', false);
    if (bad) continue;

    // A row with a date and nothing else is not an outcome of zero, it is a row
    // somebody forgot to fill in. Zero has to be typed to be meant.
    if (leads === null && bookings === null && sales === null && revenue === null) {
      problems.push({ line: lineNo, column: metricsPresent[0], message: t('outcomeImport.rowHasNoNumbers') });
      continue;
    }

    seen.set(key, lineNo);
    const shape = shapeByDate.get(date) ?? { total: 0, split: 0 };
    if (platform === null) shape.total += 1;
    else shape.split += 1;
    shapeByDate.set(date, shape);

    rows.push({ line: lineNo, date, platform, leads, bookings, sales, revenue });
  }

  // A day broken down by platform AND carrying its own total double-counts, and
  // does it silently — each row is individually plausible. Caught here as well
  // as in the database so the reader gets a line number instead of a constraint.
  for (const [date, shape] of shapeByDate) {
    if (shape.total > 0 && shape.split > 0) {
      const line = rows.find((r) => r.date === date && r.platform === null)?.line ?? 0;
      problems.push({ line, column: 'platform', message: t('outcomeImport.mixedShape', { date }) });
    }
  }

  if (rows.length === 0) {
    return { rows: [], problems, fatal: problems.length > 0 ? null : t('outcomeImport.noRows'), period: null };
  }

  // Read FROM the rows. Asking the uploader for a range as well would only ever
  // agree with the file or contradict it.
  const dates = rows.map((r) => r.date).sort();
  return {
    rows,
    problems,
    fatal: null,
    period: { start: dates[0], end: dates[dates.length - 1] },
  };
};
